'use client';
import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import type { GetServerSideProps, GetServerSidePropsResult } from 'next';

import { contentfulAPIURL } from '@/clients/contentful';
import { createApolloClient } from '@/clients/apollo';
import { queryFooter, queryNavbar } from '@/clients/queries';
import { FooterQuery, NavbarQuery } from 'types/generated';

import PageContainer from '@/components/PageContainer';
import { RadioSchedule } from '@/models/RadioTypes';
import { getRadioSchedules } from '@/clients/RadioUtils';
import ProductCard from '@/components/Store/ProductCard';
import Link from 'next/link';
import SubNavigation from '@/components/Store/SubNavigation';
import PercentageRangeSlider from 'components/src/percentageRangeSlider';

import { useProducts } from '@/utils/useProducts';
import type { ContentfulProductExtra, Product, ProductSearchBody } from '@/models/ProductTypes';
import TextInput from 'components/src/textInput/TextInput';
import { Timeout } from 'react-number-format/types/types';
import { formatISK } from '@/utils/utils';
import Button from 'components/src/button';
import ChevronDown from 'components/src/svg/ChevronDown';
import OutsideClickHandler from '@/components/common/OutsideClickHandler';
import { CATEGORY_NAME_MAP, CATEGORY_ID_MAP } from '@/config/constants';
import { getProductsExtras } from '@/utils/server-utils';
import { Log } from '@/logging/datadog';
import clsx from 'clsx';
import { calculateProductDiscountRange, formatDiscountRange } from '@/utils/discountCalculator';

function useClientReady() {
  const [ready, setReady] = React.useState(false);
  React.useEffect(() => setReady(true), []);
  return ready;
}

const PRICE_CAP = 500_000; // Upper api limit is 1_000_000_000

type Props = {
  navbar: NavbarQuery;
  footer: FooterQuery;
  category: keyof typeof CATEGORY_NAME_MAP;
  categoryId: string;
  companyOptions: { label: string; value: string }[];
  radioSchedules: Record<string, RadioSchedule[]>;
  initialProducts: Product[];
  initialFilters: ProductSearchBody;
  productsExtras: Record<string, ContentfulProductExtra>;
};

function useFilterState(category: string, categoryId: string, initial?: ProductSearchBody) {
  const router = useRouter();

  // Safe defaults + optional server-provided initial

  const filters = useMemo<ProductSearchBody>(() => {
    const init: ProductSearchBody = {
      searchTerm: '',
      itemCategory: categoryId,
      itemGroup: '',
      itemType: '',
      minPrice: 0,
      maxPrice: PRICE_CAP,
      sort: 'created_desc',
      ...(initial ?? {}),
    };
    const q = router.query;
    return {
      searchTerm: (q.q as string) ?? init.searchTerm ?? '',
      itemCategory: categoryId,
      itemGroup: (q.group as string) ?? init.itemGroup ?? '',
      itemType: (q.type as string) ?? init.itemType ?? '',
      minPrice: q.min ? Number(q.min) : init.minPrice ?? 0,
      maxPrice: q.max ? Number(q.max) : init.maxPrice ?? PRICE_CAP,
      sort: ((q.sort as string) || init.sort || 'created_desc') as ProductSearchBody['sort'],
    };
  }, [router.query, initial, categoryId]);

  const setFilters = (next: Partial<ProductSearchBody>) => {
    const merged = { ...filters, ...next };
    const query: Record<string, string | number> = { category };

    if (merged.searchTerm) query.q = merged.searchTerm;
    if (merged.itemGroup) query.group = merged.itemGroup;
    if (merged.itemType) query.type = merged.itemType;
    if (merged.minPrice && merged.minPrice > 0) query.min = merged.minPrice;
    if (merged.maxPrice && merged.maxPrice < PRICE_CAP) query.max = merged.maxPrice;
    if (merged.sort && merged.sort !== 'created_desc') query.sort = merged.sort;

    router.push({ pathname: router.pathname, query }, undefined, { shallow: true });
  };

  return { filters, setFilters };
}

const FarsimarPage = ({
  navbar,
  footer,
  category,
  categoryId,
  companyOptions,
  radioSchedules,
  initialProducts,
  initialFilters,
  productsExtras,
}: Props) => {
  const [faqData, setFaqData] = useState<[]>([]);
  const [timeoutId, setTimeoutId] = useState<Timeout>();
  const [maxPercentagePrice, setMaxPercentagePrice] = useState<number>(
    initialFilters.maxPrice ? Math.max(initialFilters.maxPrice / PRICE_CAP, 100) : 100
  );
  const [minPercentagePrice, setMinPercentagePrice] = useState<number>(
    initialFilters.minPrice ? Math.max(initialFilters.minPrice / PRICE_CAP, 100) : 0
  );
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showPriceRange, setShowPriceRange] = useState<boolean>(false);
  const [showSortPicker, setShowSortPicker] = useState<boolean>(false);
  const [showCompanyPicker, setShowCompanyPicker] = useState<boolean>(false);
  const [companyFilter, setCompanyFilter] = useState<string>('');
  const { filters, setFilters } = useFilterState(category, categoryId, initialFilters);
  const { products: clientProducts, isLoading } = useProducts(filters);
  const router = useRouter();
  const { id } = router.query;

  const sortOptions = [
    { label: 'Nýjast', value: 'created_desc' },
    { label: 'Ódýrast', value: 'price_asc' },
    { label: 'Dýrast', value: 'price_desc' },
    { label: 'A → Ö', value: 'name_asc' },
    { label: 'Ö → A', value: 'name_desc' },
  ] as { label: string; value: string }[];
  const sortOptionByValue = (value: string | undefined) => {
    if (value === undefined) {
      return null;
    }
    return sortOptions.filter((o) => o.value === value)[0];
  };

  // What does this do? Friggi asks.
  const articleRefs = useRef<{ [key: string]: HTMLLIElement | undefined }>({});
  useEffect(() => {
    if (typeof id === 'string' && articleRefs.current[id as keyof typeof articleRefs.current]) {
      articleRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [id, faqData]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const credentials = process.env.NEXT_PUBLIC_SPURT_SVARAD_CREDENTIALS;
        const response = await fetch(`${process.env.NEXT_PUBLIC_SPURT_SVARAD_ENDPOINT}Algengar_spurningar%22%7D`, {
          headers: { Authorization: `Basic ${credentials}` },
        });
        const jsonData = await response.json();
        setFaqData(Array.isArray(jsonData.articles) ? jsonData.articles : []);
      } catch (error) {
        Log.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const changePriceRange = (max: number, min?: number) => {
    setMaxPercentagePrice(max);
    setMinPercentagePrice(min || 0);
    clearTimeout(timeoutId);
    const handler = () =>
      setFilters({
        minPrice: Number(Math.floor(((min || 0) * PRICE_CAP) / 100)),
        maxPrice: Number(Math.floor((max * PRICE_CAP) / 100)),
      });
    setTimeoutId(setTimeout(handler, 300));
  };

  const getSorter = () => {
    if (filters.sort === 'created_desc') {
      return (a: Product, b: Product) => b.productCreatedDate.localeCompare(a.productCreatedDate);
    }
    if (filters.sort === 'price_asc') {
      return (a: Product, b: Product) => a.productPrice - b.productPrice;
    }
    if (filters.sort === 'price_desc') {
      return (a: Product, b: Product) => b.productPrice - a.productPrice;
    }
    if (filters.sort === 'name_asc') {
      return (a: Product, b: Product) => a.productName.localeCompare(b.productName);
    }
    if (filters.sort === 'name_desc') {
      return (a: Product, b: Product) => b.productName.localeCompare(a.productName);
    }
    return () => 0;
  };

  // First paint: server data; after SWR resolves, prefer clientProducts
  const items: Product[] = (clientProducts?.length ? clientProducts : initialProducts) ?? [];

  const companyOptionByValue = (value: string | undefined) => {
    if (value === undefined) {
      return null;
    }
    return companyOptions.filter((o) => o.value === value)[0];
  };

  return (
    <PageContainer
      title="Sýn | Vefverslun"
      metaTags={{
        title: 'Farsímar | Vefverslun Sýnar',
        url: 'https://www.syn.is/vefverslun/farsimar',
        keywords: 'syn,farsímar,vefverslun,sýn',
        image: { url: '/android-chrome-192x192.png' },
      }}
      navbar={navbar}
      footer={footer}
      radioSchedules={radioSchedules}
    >
      <div className="col-span-full my-[128px]">
        <div className="mx-8 mt-16 w-full max-w-6xl px-4 text-start md:mx-auto">
          <SubNavigation category={category} />
        </div>

        <div className="mx-8 mt-16 w-full max-w-6xl px-4  md:mx-auto">
          <h1 className="my-48 text-2xl font-bold">{CATEGORY_NAME_MAP[category]}</h1>
        </div>

        {/* Filters */}
        <div className="mt-8 w-full max-w-6xl px-8 md:mx-auto">
          <div className="flex flex-col justify-between gap-24 sm:flex-row">
            <div className="flex flex-col gap-24 sm:flex-row">
              <div className="">
                <TextInput
                  id="search-by-name"
                  name="Nafn"
                  placeholder="Leit"
                  label="Nafn"
                  type="text"
                  className="border-[#E1E2E3] font-bold"
                  onChange={(value) => {
                    const handler = () => setFilters({ searchTerm: value });
                    clearTimeout(timeoutId);
                    setTimeoutId(setTimeout(handler, 300));
                  }}
                />
              </div>
              {/* Combined filter controls */}
              <div className="flex flex-col md:flex-row md:gap-4">
                {/* Mobile: Filter button */}
                {useClientReady() && window.innerWidth < 768 && (
                  <div className="flex justify-between md:hidden">
                    <Button
                      label="Síur"
                      variant="surface"
                      bordered
                      iconPosition="right"
                      icon={<ChevronDown />}
                      onClick={() => setShowMobileFilters(!showMobileFilters)}
                      className="bg-white text-black border-gray-200 hover:bg-gray-50 border"
                    />
                    <OutsideClickHandler className="flex flex-col" onClick={() => setShowSortPicker(false)}>
                      <div className="flex w-fit flex-col">
                        <Button
                          label={filters.sort ? sortOptionByValue(filters.sort)?.label || 'Veldu röðun' : 'Veldu röðun'}
                          variant="surface"
                          iconPosition="right"
                          icon={<ChevronDown />}
                          onClick={() => setShowSortPicker(!showSortPicker)}
                        />
                        <div
                          className={`relative -bottom-8 left-0 flex justify-end  ${
                            showSortPicker ? 'block' : 'hidden'
                          }`}
                        >
                          <div className="absolute z-30 flex w-[200px] flex-col rounded-24 bg-white-50 shadow-sm">
                            {sortOptions.map((o) => (
                              <Button
                                key={o.value}
                                label={o.label}
                                variant="surface"
                                labelClasses="whitespace-nowrap text-left"
                                onClick={() =>
                                  setFilters({
                                    sort: o.value as 'price_asc' | 'price_desc' | 'name_asc' | 'name_desc' | undefined,
                                  })
                                }
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </OutsideClickHandler>
                  </div>
                )}
                {/* Company Picker */}
                <div className="mt-16 flex md:mt-0">
                  <OutsideClickHandler
                    className={`flex flex-col ${
                      showMobileFilters || showCompanyPicker ? 'block' : 'hidden'
                    } mr-16 md:mr-24 md:flex`}
                    onClick={() => setShowCompanyPicker(false)}
                  >
                    <div className="flex w-fit flex-col">
                      <Button
                        label={companyOptionByValue(companyFilter)?.label || 'Framleiðandi'}
                        variant="surface"
                        bordered
                        iconPosition="right"
                        icon={<ChevronDown />}
                        onClick={() => setShowCompanyPicker(!showCompanyPicker)}
                      />
                      <div
                        className={`relative -bottom-8 left-0 flex justify-end ${
                          showCompanyPicker ? 'block' : 'hidden'
                        }`}
                      >
                        <div className="absolute z-30 flex w-[200px] flex-col rounded-24 bg-white-50 shadow-sm">
                          {companyOptions.map((o) => (
                            <Button
                              key={o.value}
                              label={o.label}
                              variant="surface"
                              labelClasses="whitespace-nowrap text-left"
                              onClick={() => {
                                setCompanyFilter(o.value);
                                setShowCompanyPicker(false);
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </OutsideClickHandler>

                  {/* Price Range */}
                  <OutsideClickHandler
                    className={`flex flex-col ${showMobileFilters || showPriceRange ? 'block' : 'hidden'} md:flex`}
                    onClick={() => setShowPriceRange(false)}
                  >
                    <Button
                      label="Verðbil"
                      variant="surface"
                      bordered
                      iconPosition="right"
                      icon={<ChevronDown />}
                      onClick={() => setShowPriceRange(!showPriceRange)}
                    />
                    <div className={`relative -bottom-8 ${showPriceRange ? 'block' : 'hidden'}`}>
                      <div
                        className={clsx(
                          'absolute z-30 transform rounded-24 bg-white-50 px-24 pb-16 pt-32 shadow-sm',
                          useClientReady() && window.innerWidth < 768 ? ' right-0  ' : ' left-1/2  -translate-x-1/2  '
                        )}
                      >
                        <div
                          className="z-30 flex w-[200px] flex-col items-center rounded-full"
                          onTouchStart={(e) => e.stopPropagation()}
                          onTouchMove={(e) => e.stopPropagation()}
                          onTouchEnd={(e) => e.stopPropagation()}
                        >
                          <PercentageRangeSlider
                            min={minPercentagePrice}
                            max={maxPercentagePrice}
                            minLabel={formatISK((minPercentagePrice * PRICE_CAP) / 100)}
                            maxLabel={formatISK((maxPercentagePrice * PRICE_CAP) / 100)}
                            minGap={5}
                            onChange={changePriceRange}
                          />
                        </div>
                      </div>
                    </div>
                  </OutsideClickHandler>
                </div>
              </div>
            </div>
            {useClientReady() && window.innerWidth >= 768 && (
              <OutsideClickHandler className="flex flex-col" onClick={() => setShowSortPicker(false)}>
                <div className="flex w-fit flex-col">
                  <Button
                    label={filters.sort ? sortOptionByValue(filters.sort)?.label || 'Veldu röðun' : 'Veldu röðun'}
                    variant="surface"
                    iconPosition="right"
                    icon={<ChevronDown />}
                    onClick={() => setShowSortPicker(!showSortPicker)}
                  />
                  <div className={`relative -bottom-8 left-0 flex justify-end  ${showSortPicker ? 'block' : 'hidden'}`}>
                    <div className="absolute z-30 flex w-[200px] flex-col rounded-24 bg-white-50 shadow-sm">
                      {sortOptions.map((o) => (
                        <Button
                          key={o.value}
                          label={o.label}
                          variant="surface"
                          labelClasses="whitespace-nowrap text-left"
                          onClick={() =>
                            setFilters({
                              sort: o.value as 'price_asc' | 'price_desc' | 'name_asc' | 'name_desc' | undefined,
                            })
                          }
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </OutsideClickHandler>
            )}
          </div>
        </div>

        {/* Products grid */}
        <div className="mx-auto mb-24 mt-8 w-full max-w-6xl px-4">
          {isLoading && !items.length ? (
            <p className="text-gray-500 text-sm">Sæki vörur…</p>
          ) : (
            <div className="gap-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
              {items
                .sort(getSorter())
                .filter((p) => (!!companyFilter ? p.productGroup === companyFilter : true))
                .filter((p) => (productsExtras[p.productId]?.images?.length ?? 0) > 0)
                .map((product) => {
                  const discountRange = calculateProductDiscountRange(product);
                  const formattedDiscount = formatDiscountRange(discountRange);

                  return (
                    <Link
                      key={product.productId}
                      href={`/vefverslun/um-voru/${encodeURIComponent(product.productId)}`}
                      className="m-8 block focus:outline-none focus:ring-2 focus:ring-offset-2"
                    >
                      <ProductCard
                        name={product.productName}
                        category={product.productCategory}
                        group={product.productGroup}
                        price={product.productPrice}
                        oldPrice={product.productDiscountPrice}
                        available={product.productAvailability > 0 && !productsExtras[product.productId]?.isOutOfStock}
                        images={(productsExtras[product.productId]?.images || []).slice(0, 3)}
                        energySticker={productsExtras[product.productId]?.energySticker || 'none'}
                        showDynamicDiscountLabel={productsExtras[product.productId]?.showDynamicDiscountLabel !== false}
                        created={product.productCreatedDate}
                        hideNewSticker={productsExtras[product.productId]?.hideNewSticker}
                        discountRange={formattedDiscount}
                      />
                    </Link>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async (ctx): Promise<GetServerSidePropsResult<Props>> => {
  // Build filters from the incoming URL (server-side)
  const q = ctx.query;

  const category = q.category as keyof typeof CATEGORY_NAME_MAP;
  const categoryId = CATEGORY_ID_MAP[category];

  if (!categoryId) {
    return {
      redirect: {
        destination: '/404',
        permanent: false,
      },
    };
  }

  const initialFilters: ProductSearchBody = {
    searchTerm: (q.q as string) || '',
    itemCategory: categoryId,
    itemGroup: (q.group as string) || '',
    itemType: (q.type as string) || '',
    minPrice: q.min ? Number(q.min) : 0,
    maxPrice: q.max ? Number(q.max) : PRICE_CAP,
    sort: ((q.sort as string) || 'created_desc') as ProductSearchBody['sort'],
  };

  try {
    const apiUrl = contentfulAPIURL(
      process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID || '',
      process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT || ''
    );
    const apolloClient = createApolloClient(apiUrl, process.env.NEXT_PUBLIC_CONTENTFUL_CD_TOKEN || '');

    const baseUrl =
      process.env.NEXT_PUBLIC_BASE_URL ||
      (process.env.VERCEL_URL ? `https://${process.env.VERCEL_URL}` : 'http://localhost:3000');

    // Server-side fetch to your internal API (expects Product[] directly)
    let initialProducts = [] as Product[];
    if (!!categoryId) {
      const resp = await fetch(`${baseUrl}/api/store/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(initialFilters),
      });
      const responseData = await resp.json();

      if (Array.isArray(responseData)) {
        initialProducts = responseData as Product[];
      } else {
        initialProducts = [];
      }
    }

    const productIds = Array.isArray(initialProducts) ? initialProducts.map((p) => p.productId) : [];
    const productsExtras = await getProductsExtras(productIds, apolloClient);

    const footer = await queryFooter(apolloClient);
    const navbar = await queryNavbar(apolloClient);

    const companies = [
      ...new Set(
        initialProducts
          .map((p) => p.productGroup)
          .filter((p) => !!p)
          .sort((a, b) => a.localeCompare(b))
      ),
    ];

    const companyOptions = [
      { label: 'Framleiðandi', value: '' },
      ...companies.map((company) => ({ label: company, value: company })),
    ] as { label: string; value: string }[];

    return {
      props: {
        navbar: navbar.data,
        footer: footer.data,
        category: category,
        categoryId: categoryId,
        companyOptions: companyOptions as { label: string; value: string }[],
        radioSchedules: await getRadioSchedules(),
        initialProducts: Array.isArray(initialProducts) ? initialProducts : [],
        initialFilters,
        productsExtras,
      },
    };
  } catch (err) {
    Log.error((err as Error).message || '', err);

    // Return minimal but correctly-typed props so the page still renders
    return {
      props: {
        navbar: {} as NavbarQuery,
        footer: {} as FooterQuery,
        category: category,
        categoryId: categoryId,
        companyOptions: [] as { label: string; value: string }[],
        radioSchedules: {},
        initialProducts: [],
        initialFilters: {
          searchTerm: '',
          itemCategory: categoryId,
          itemGroup: '',
          itemType: '',
          minPrice: 0,
          maxPrice: PRICE_CAP,
          sort: 'created_desc',
        },
        productsExtras: {},
      },
    };
  }
};

export default FarsimarPage;
